// KeyboardShortcuts — globale g-prefix navigatie + ?-help modal.
// Bewust géén shortcut handling bij input/textarea focus om typen niet te breken.

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

interface Shortcut { keys: string; label: string; to?: string; section: string }

const SHORTCUTS: Shortcut[] = [
  { section: "Globaal", keys: "⌘K / Ctrl+K", label: "Command-bar openen" },
  { section: "Globaal", keys: "?", label: "Deze help tonen" },
  { section: "Navigatie", keys: "g d", label: "Dashboard", to: "/dashboard" },
  { section: "Navigatie", keys: "g o", label: "Orders", to: "/orders" },
  { section: "Navigatie", keys: "g w", label: "Warehouse", to: "/warehouse" },
  { section: "Navigatie", keys: "g p", label: "Producten", to: "/producten" },
  { section: "Navigatie", keys: "g i", label: "Inkoop", to: "/inkoop" },
  { section: "Navigatie", keys: "g v", label: "Verkopen", to: "/verkopen" },
  { section: "Navigatie", keys: "g k", label: "Klanten", to: "/klanten" },
  { section: "Navigatie", keys: "g f", label: "Finance", to: "/finance" },
  { section: "Navigatie", keys: "g r", label: "Rapportages", to: "/rapportages" },
  { section: "Navigatie", keys: "g t", label: "Taken", to: "/taken" },
  { section: "Navigatie", keys: "g c", label: "Reconciliation", to: "/reconciliation" },
  { section: "Navigatie", keys: "g b", label: "Beheer", to: "/beheer" },
  { section: "Navigatie", keys: "g v", label: "Project voortgang", to: "/progress" },
];

const NAV_MAP: Record<string, string> = Object.fromEntries(
  SHORTCUTS.filter((s) => s.to).map((s) => [s.keys.replace("g ", ""), s.to!]),
);

function isTypingTarget(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null;
  if (!t) return false;
  const tag = t.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if ((t as HTMLElement).isContentEditable) return true;
  return false;
}

export function KeyboardShortcuts() {
  const [help, setHelp] = useState(false);
  const [gPrefix, setGPrefix] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let prefixTimer: ReturnType<typeof setTimeout> | null = null;
    const onKey = (e: KeyboardEvent) => {
      if (isTypingTarget(e)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "?" || (e.shiftKey && e.key === "/")) {
        e.preventDefault();
        setHelp((v) => !v);
        return;
      }
      if (e.key === "Escape") { setHelp(false); setGPrefix(false); return; }
      if (gPrefix) {
        const target = NAV_MAP[e.key.toLowerCase()];
        if (target) { e.preventDefault(); navigate(target); }
        setGPrefix(false);
        if (prefixTimer) { clearTimeout(prefixTimer); prefixTimer = null; }
        return;
      }
      if (e.key.toLowerCase() === "g") {
        setGPrefix(true);
        prefixTimer = setTimeout(() => setGPrefix(false), 1200);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      if (prefixTimer) clearTimeout(prefixTimer);
    };
  }, [gPrefix, navigate]);

  const sections: Array<{ section: string; items: Shortcut[] }> = [];
  for (const s of SHORTCUTS) {
    const last = sections[sections.length - 1];
    if (last && last.section === s.section) last.items.push(s);
    else sections.push({ section: s.section, items: [s] });
  }

  return (
    <>
      {gPrefix && (
        <div style={{
          position: "fixed", bottom: 20, left: 20, zIndex: 150,
          padding: "6px 12px", background: "var(--color-bg-elevated, #334155)",
          color: "var(--color-text)", border: "1px solid var(--color-border)",
          borderRadius: 6, fontSize: 12,
        }}>
          <span style={{ opacity: 0.7 }}>g …</span> wachten op tweede toets (d/o/w/p/i/m/k/f/r/t/c/b/v)
        </div>
      )}
      {help && createPortal(
        <div className="cmdk-backdrop" onClick={() => setHelp(false)}>
          <div className="cmdk-modal" onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: "16px 18px", borderBottom: "1px solid var(--color-border)", fontSize: 16, fontWeight: 600 }}>
              Sneltoetsen
            </div>
            <div className="cmdk-results" style={{ padding: "8px 0" }}>
              {sections.map((sec) => (
                <div key={sec.section}>
                  <div className="cmdk-group-label">{sec.section}</div>
                  {sec.items.map((s) => (
                    <div key={s.keys + s.label} className="cmdk-item">
                      <span className="cmdk-label">{s.label}</span>
                      <span className="cmdk-hint" style={{
                        fontFamily: "var(--font-mono, monospace)",
                        padding: "2px 6px",
                        background: "var(--color-bg-elevated, #334155)",
                        borderRadius: 4,
                      }}>{s.keys}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div className="cmdk-footer">
              <span>esc sluit</span>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
